import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { ColumnConfig } from '@art-of-hospitality-solutions-llc/ui-kit/components/data-table';
import {
  toastError,
  toastSuccess,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/toast';
import { ResourceTableView } from '@Components/ResourceTable';
import { useResourceTableController } from '@Components/ResourceTable/useResourceTableController';
import { buildFilterParams } from '@Utils/buildFilterParams';
import { useFlowCategories } from '@Hooks/useFlowCategories';
import { Pencil, Percent, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import ConfirmationDialog from '@Components/ConfirmationDialog';
import service from '../../../service';
import { FlowCategory } from '../../../types/flowCategory';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@Contexts/auth';
import { categoryIcons } from '@Constants/iconMap';

const FLOW_CATEGORY_FIELD_MAPPING: Record<string, string> = {
  name: 'filter[name]',
  color: 'filter[color]',
  icon: 'filter[icon]',
};

const columnConfigs: ColumnConfig<FlowCategory>[] = [
  {
    id: 'name',
    headerLabel: 'Name',
    accessor: (row) => row.name ?? '-',
    type: 'text',
  },
  {
    id: 'color',
    headerLabel: 'Color',
    accessor: (row) => row.color ?? '-',
    type: 'text',
    cell: (info) => {
      const value = info.getValue() as string;

      return (
        <div className="flex items-center gap-2">
          <div
            className="h-6 w-12 rounded border border-black"
            style={{
              backgroundColor: value || 'transparent',
            }}
          />
          {/* <span>{value || '-'}</span> */}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    id: 'icon',
    headerLabel: 'Icon',
    accessor: (row) => row.icon ?? '',
    type: 'text',
    cell: (info) => {
      const iconKey = info.getValue() as string;
      const iconData = categoryIcons[iconKey];

      return (
        <div className="flex items-center gap-2">
          {iconData?.icon}
          {/* <span>{iconData?.label || iconKey || '-'}</span> */}
        </div>
      );
    },
    enableSorting: false,
  },
];

type FlowCategoriesListProps = {
  onEditClick?: (row: { original: FlowCategory }) => void;
  onCreateClick?: () => void;
};

export default function FlowCategoriesList({
  onEditClick,
  onCreateClick,
}: FlowCategoriesListProps = {}) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [flowCategoryToDelete, setFlowCategoryToDelete] =
    useState<FlowCategory | null>(null);

  const { user } = useAuth();

  const client = useQueryClient();

  const controller = useResourceTableController<FlowCategory>({
    columnsConfig: columnConfigs,
    defaultSort: {
      id: 'name',
      desc: false,
    },
    buildFilterParams: (filters) =>
      buildFilterParams(filters, {
        fieldMapping: FLOW_CATEGORY_FIELD_MAPPING,
      }),
    quickFilterFields: [{ field: 'name', label: 'Name' }],
    initialPageSize: 10,
    rowActions: (row) => (
      <div className="flex gap-1">
        <Button
          variant="ghost"
          size="sm"
          // asChild
          onClick={(e) => {
            e.stopPropagation();
            onEditClick({ original: row });
          }}
        >
          <Pencil className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(row);
          }}
        >
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    ),
  });

  const {
    data: flowCategories,
    status,
    isError,
    error,
  } = useFlowCategories({ searchParams: controller.searchParams });

  useEffect(() => {
    if (isError && error) {
      toastError(
        error instanceof Error
          ? error.message
          : 'Failed to load Flow Categories',
      );
    }
  }, [isError, error]);

  const rows: FlowCategory[] = flowCategories?.data ?? [];
  const meta = flowCategories?.meta;

  const handleDeleteClick = (flowCategory: FlowCategory) => {
    setFlowCategoryToDelete(flowCategory);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!flowCategoryToDelete) return;

    try {
      await service.deleteFlowCategory(flowCategoryToDelete.id, user?.id);

      toastSuccess(`"${flowCategoryToDelete.name}" has been deleted.`);
      setDeleteDialogOpen(false);
      setFlowCategoryToDelete(null);
      client.invalidateQueries({ queryKey: ['flow-categories'] });
    } catch (err) {
      toastError(
        err instanceof Error ? err.message : 'Failed to delete Flow Category',
      );
    }
  };

  return (
    <div className="mt-2 space-y-4">
      <ResourceTableView<FlowCategory>
        label="Flow Categories"
        controller={controller}
        data={rows}
        meta={meta}
        status={status}
        summaryIcon={<Percent className="h-4 w-4 text-zinc-500" />}
        summaryLabel="Total Flow Categories: "
        renderExtraActions={() => (
          <Button
            variant="primary"
            onClick={(e) => {
              e.stopPropagation();
              onCreateClick();
            }}
          >
            <Plus className="mr-2 h-4 w-4" />
            Create Flow Category
          </Button>
        )}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onSubmit={handleDeleteConfirm}
        match={flowCategoryToDelete?.name ?? ''}
        title="Delete Flow Category"
        description={`Are you sure you want to delete "${flowCategoryToDelete?.name}"? This action cannot be undone.`}
        matchLabel="Type the flow category name to confirm"
        submitLabel="Delete"
        // isLoading={deleteMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}
